import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MailEditor } from '@adornis/mails/client/x-mail-editor.js';
import { MailTemplate } from '@adornis/mails/entities/mail-template.js';
import { Recipient } from '@adornis/mails/entities/Recipient.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { UserListAPI } from '../../users/list/api/UserListAPI.js';
import type { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { UserEmailsAPI } from '../UserEmailsAPI.js';
import { FreeTextMailVariables, type FreeMailAdditionalInformation } from './sendFreeTextMails.js';

const SendFreeTextEmailDialogContent = stateful(
  (props: {
    userDataInterface: UserListSelectionInterface | null;
    resolve: (mailTemplate: {
      mail: MailTemplate | null;
      hasRecipients: boolean;
      sendToSorgeberechtigte: boolean;
    }) => void;
    reject: () => void;
  }) => {
    const [mailTemplate, setMailTemplate] = useState(new MailTemplate({ emailType: 'free-text' }));

    const { value: recipients, loading: recipientsLoading } = useQuery(
      () =>
        UserListAPI.getBySelection(props.userDataInterface)({
          email: 1,
          _id: 1,
          sorgeberechtigteEltern: { _id: 1, email: 1 },
        }),
      [props.userDataInterface],
    );

    // preview only - the server applies the same remap to the selection when sending
    const remapToSbS = (user: { email: string; sorgeberechtigteEltern?: Maybe<{ email: string }[]> }) => {
      if (!user.sorgeberechtigteEltern || user.sorgeberechtigteEltern.length === 0) {
        return [user.email];
      }
      return user.sorgeberechtigteEltern.map(sbs => sbs.email);
    };

    const [mapToSbS, setMapToSbS] = useState(false);
    const [checkedSBS, setCheckedSBS] = useState(false);

    useEffect(() => {
      /**
       * Check if any of the persons has sbs attached
       * We do not want to sent the emails to the children probably
       */
      const possibleSbS = recipients?.flatMap(user => user.sorgeberechtigteEltern ?? []);

      /**
       * If we have selected students which have sbs attached. Prompt the user if he would like to map each child to all sbs emails
       */
      if (!checkedSBS && possibleSbS && possibleSbS.length > 0) {
        confirmDialog(
          'Es wurden Kinder in der Auswahl identifiziert. Möchten Sie die E-Mail statt an die Kinder, an deren jeweilige Sorgeberechtigte senden wenn diese vorhanden sind?',
          {
            acceptText: 'Ja. Die Sorgeberechtigten anschreiben.',
            declineText: 'Nein. E-Mail-Adressen beibehalten.',
          },
          {
            modalBoxClasses: 'max-w-2xl',
          },
        ).then(res => {
          setCheckedSBS(true);
          setMapToSbS(res);
        });
      }
    }, [recipients]);

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Freitext-Mail senden',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}
        ${
          !recipientsLoading
            ? MailEditor({
                recipients: mapToSbS
                  ? recipients?.flatMap(user => remapToSbS(user).map(r => new Recipient({ to: r })))
                  : (recipients?.map(user => new Recipient({ to: user.email })) ?? []),
                value: mailTemplate,
                onChange: value => setMailTemplate(value),
                // the preview renders the engine's default layout, whose default logo is Adornis'; the sent mail is
                // wrapped in the Martinus template on the server instead
                buildMailOptions: { logoConfig: false },
                exampleVariableValues: {
                  ...Object.fromEntries(Object.values(FreeTextMailVariables).map(variable => [variable, variable])),
                  [FreeTextMailVariables.BUCHUNGS_REFERENZ]: '"AAAAAA 1234567 00000 Max Mustermann"',
                  [FreeTextMailVariables.PORTAL_LINK]: `<a href="${A.absoluteUrl()}"> ${A.absoluteUrl()} </a>`,
                },
              })
            : html`<span class="loading loading-spinner loading-lg"></span>`
        }

        <button
          class="btn btn-primary"
          @click=${() =>
            props.resolve({
              mail: mailTemplate,
              hasRecipients: !!recipients?.length,
              sendToSorgeberechtigte: mapToSbS,
            })}
        >
          Senden
        </button>
      </div>
    `;
  },
);

export const sendFreeTextEmailDialog = async (
  userDataInterface: UserListSelectionInterface,
  additionalInformation?: FreeMailAdditionalInformation,
) => {
  const data = await showDialog<{ mail: MailTemplate | null; hasRecipients: boolean; sendToSorgeberechtigte: boolean }>(
    {
      content: (resolve, reject) =>
        SendFreeTextEmailDialogContent({
          userDataInterface,
          resolve,
          reject,
        }),
      modalBoxClasses: 'w-[95vw] h-[95vh] max-w-[95vw] max-h-[95vh]',
      modal: true,
    },
  );

  if (!data.mail || !data.hasRecipients) {
    return;
  }

  const mail = data.mail;
  await guardDialog(() =>
    UserEmailsAPI.sendFreeTextMail(userDataInterface, data.sendToSorgeberechtigte, mail, additionalInformation)(),
  );
};
